import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';

import { HandbookPdfButton } from './HandbookPdfButton';
import { HandbookBlock } from './HandbookBlocks';
import { useHandbook } from './HandbookContext';
import { pagesInGroup } from './handbookContent';
import type { HandbookPageDef } from './handbookTypes';

/** The PDF's suggested file name comes from the document title. */
const PDF_TITLE = 'RapidSOS Studio Handbook';

function printAnchor(page: Pick<HandbookPageDef, 'group' | 'slug'>): string {
  return `print-${page.group}-${page.slug}`;
}

export function HandbookPrint() {
  const handbook = useHandbook();
  const printedOn = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    const previousTitle = document.title;
    document.title = PDF_TITLE;
    document.documentElement.classList.add('handbook-printing');
    return () => {
      document.title = previousTitle;
      document.documentElement.classList.remove('handbook-printing');
    };
  }, []);

  return (
    <div className="handbook-print min-h-screen bg-background text-foreground">
      <div className="sticky top-0 z-40 flex items-center gap-3 border-b border-border bg-card px-4 py-3 print:hidden">
        <Button asChild size="sm" variant="ghost">
          <Link to="/">
            <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
            Back to the handbook
          </Link>
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={() => window.print()}>Print</Button>
        <HandbookPdfButton automatic={new URLSearchParams(window.location.search).get('download') === '1'} />
      </div>

      <div className="mx-auto max-w-[46rem] px-8 py-12 print:max-w-none print:p-0">
        <section className="handbook-sheet flex min-h-[80vh] flex-col justify-center print:min-h-[9in]">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">RapidSOS · Internal</div>
          <h1 className="mt-4 text-5xl font-bold tracking-tight">Studio Handbook</h1>
          <p className="mt-4 max-w-xl text-lg leading-8 text-muted-foreground">How Studio works today, how to extend it with integrations, and how to operate it.</p>
          <p className="mt-10 text-sm text-muted-foreground">
            {handbook.pages.length} guides · printed {printedOn}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">For RapidSOS people with Handbook Access. Do not share outside RapidSOS.</p>
        </section>

        <section className="handbook-sheet py-6">
          <h2 className="text-2xl font-semibold tracking-tight">Contents</h2>
          <ol className="mt-6 space-y-6">
            {handbook.groups.map((group) => (
              <li key={group.id}>
                <div className="text-sm font-semibold">{group.label}</div>
                <p className="text-xs text-muted-foreground">{group.blurb}</p>
                <ol className="mt-2 space-y-1">
                  {pagesInGroup(handbook, group.id).map((page) => (
                    <li key={page.slug} className="flex items-baseline gap-2 text-sm">
                      <a href={`#${printAnchor(page)}`} className="text-foreground no-underline">
                        {page.title}
                      </a>
                      <span className="flex-1 border-b border-dotted border-border" />
                      <span className="text-xs text-muted-foreground">{page.minutes} min</span>
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        </section>

        {handbook.groups.flatMap((group) =>
          pagesInGroup(handbook, group.id).map((page) => (
            <article key={`${page.group}/${page.slug}`} id={printAnchor(page)} className="handbook-sheet py-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.label}</div>
              <h1 className="mt-2 text-3xl font-bold tracking-tight">{page.title}</h1>
              <p className="mt-3 text-base leading-7 text-muted-foreground">{page.summary}</p>
              <div className="mt-4 border-b border-border" />
              {page.blocks.map((block, index) => (
                <HandbookBlock key={index} block={block} />
              ))}
            </article>
          )),
        )}
      </div>
    </div>
  );
}
